
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Settings, CalendarDays, Medal, Users, FileBadge, MessageSquare, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import LanguageSwitcher from '@/components/ui-elements/language-switcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AdminHeaderDesktopMenuProps {
  handleGoToDashboard: () => void;
  handleOpenDialog: () => void;
  handleOpenAppSettings: () => void;
  handleOpenEventsManagement: () => void;
  handleOpenBadgesManagement: () => void;
  handleLogout: () => Promise<void>;
}

const AdminHeaderDesktopMenu: React.FC<AdminHeaderDesktopMenuProps> = ({
  handleGoToDashboard,
  handleOpenDialog,
  handleOpenAppSettings,
  handleOpenEventsManagement,
  handleOpenBadgesManagement,
  handleLogout
}) => {
  const { t } = useTranslation(['admin', 'navigation', 'common']);
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const linkClass = 'flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors';
  const activeClass = 'bg-red-400/10 text-white hover:bg-red-300/15';
  const inactiveClass = 'text-muted-foreground hover:text-foreground hover:bg-accent';

  return (
    <nav className="hidden md:flex ml-10 items-center space-x-1" aria-label={t('admin:adminNavigation')}>
      <Link 
        to="/admin" 
        className={`${linkClass} ${isActive('/admin') ? activeClass : inactiveClass}`}
        aria-current={isActive('/admin') ? 'page' : undefined}
      >
        <Users size={16} />
        {t('admin:users')}
      </Link>
      
      <Link
        to="/admin/applications"
        className={`${linkClass} ${isActive('/admin/applications') ? activeClass : inactiveClass}`}
        aria-current={isActive('/admin/applications') ? 'page' : undefined}
      >
        <FileBadge size={16} />
        {t('admin:applications')}
      </Link>
      
      <Link
        to="/admin/quick-interest"
        className={`${linkClass} ${isActive('/admin/quick-interest') ? activeClass : inactiveClass}`}
        aria-current={isActive('/admin/quick-interest') ? 'page' : undefined}
      >
        <MessageSquare size={16} />
        {t('navigation:partner')}
      </Link>
      
      {/* Content dropdown for Events, Badges and Quests */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={`${linkClass} ${
              isActive('/admin/events') || isActive('/admin/badges') || isActive('/admin/quests') 
                ? activeClass 
                : inactiveClass
            }`}
            aria-expanded="false"
          >
            <Gift size={16} />
            {t('admin:content')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48 bg-[#1A1F2C] border-gray-700 text-white">
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link 
                to="/admin/events" 
                className="cursor-pointer flex items-center gap-2 focus:bg-red-400/10"
              >
                <CalendarDays size={16} />
                {t('admin:events')}
              </Link>
            </DropdownMenuItem>
            
            <DropdownMenuItem asChild>
              <Link 
                to="/admin/badges" 
                className="cursor-pointer flex items-center gap-2 focus:bg-red-400/10"
              >
                <Medal size={16} />
                {t('admin:badges')}
              </Link>
            </DropdownMenuItem>
            
            <DropdownMenuItem asChild>
              <Link 
                to="/admin/quests" 
                className="cursor-pointer flex items-center gap-2 focus:bg-red-400/10"
              >
                <Gift size={16} />
                {t('admin:quests')}
              </Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <Link 
        to="/admin/settings" 
        className={`${linkClass} ${isActive('/admin/settings') ? activeClass : inactiveClass}`}
        aria-current={isActive('/admin/settings') ? 'page' : undefined}
      >
        <Settings size={16} />
        {t('admin:settings')}
      </Link>
      
      <div className="ml-4 border-l border-white/10 pl-4 flex items-center">
        <LanguageSwitcher variant="ghost" className="mr-2" aria-label={t('language:changeLanguage')} />
        
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleGoToDashboard}
          className="focus:ring-2 focus:ring-offset-1 focus:ring-red-400 focus:outline-none"
        >
          {t('navigation:userDashboard')}
        </Button>
        
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleLogout} 
          className="text-red-400 hover:text-red-300 focus:ring-2 focus:ring-offset-1 focus:ring-red-400 focus:outline-none"
        >
          {t('admin:logout')}
        </Button>
      </div>
    </nav>
  );
};

export default AdminHeaderDesktopMenu;
